export type WorkoutTimerMode = 'stopwatch' | 'countdown';
export type WorkoutTimerStatus = 'idle' | 'running' | 'paused' | 'finished';

/** Clock-based timing, independent of the DOM and callback frequency. */
export class WorkoutTimer {
  private mode: WorkoutTimerMode = 'stopwatch';
  private status: WorkoutTimerStatus = 'idle';
  private durationSeconds = 30;
  private accumulatedMs = 0;
  private startedAt: number | null = null;

  constructor(private now = Date.now, private onComplete = () => {}) {}

  private elapsedMs(): number {
    return this.accumulatedMs + (this.startedAt === null ? 0 : Math.max(0, this.now() - this.startedAt));
  }

  snapshot() {
    let elapsedMs = this.elapsedMs();
    if (this.mode === 'countdown' && this.status === 'running' && elapsedMs >= this.durationSeconds * 1000) {
      elapsedMs = this.durationSeconds * 1000;
      this.accumulatedMs = elapsedMs;
      this.startedAt = null;
      this.status = 'finished';
      this.onComplete();
    }
    return {
      mode: this.mode, status: this.status, durationSeconds: this.durationSeconds,
      seconds: this.mode === 'stopwatch' ? Math.floor(elapsedMs / 1000)
        : Math.max(0, Math.ceil(this.durationSeconds - elapsedMs / 1000)),
    };
  }

  start(): void {
    if (this.snapshot().status === 'running') return;
    if (this.status === 'finished') this.accumulatedMs = 0;
    this.startedAt = this.now();
    this.status = 'running';
  }

  pause(): void {
    if (this.snapshot().status !== 'running') return;
    this.accumulatedMs = this.elapsedMs();
    this.startedAt = null;
    this.status = 'paused';
  }

  reset(): void {
    this.accumulatedMs = 0;
    this.startedAt = null;
    this.status = 'idle';
  }

  setMode(mode: WorkoutTimerMode): boolean {
    if (this.snapshot().status === 'running') return false;
    if (this.mode !== mode) {
      this.mode = mode;
      this.reset();
    }
    return true;
  }

  setDuration(seconds: number): boolean {
    if (this.snapshot().status === 'running' || !Number.isSafeInteger(seconds)
      || seconds <= 0 || !Number.isSafeInteger(seconds * 1000)) return false;
    if (this.durationSeconds !== seconds) {
      this.durationSeconds = seconds;
      this.reset();
    }
    return true;
  }
}

export function formatTimerSeconds(seconds: number): string {
  return `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
}

const statusLabels: Record<WorkoutTimerStatus, string> = {
  idle: 'Ready', running: 'Running', paused: 'Paused', finished: 'Time’s up',
};

/** Owns one element for the entire workout visit, including room rerenders. */
export class WorkoutTimerWidget {
  private element: HTMLDetailsElement;
  private timer = new WorkoutTimer(Date.now, () => this.playChime());
  private events = new AbortController();
  private interval: ReturnType<typeof setInterval> | null = null;
  private restoreFocus: HTMLElement | null = null;
  private audio: AudioContext | null = null;
  private wakeLock: WakeLockSentinel | null = null;
  private wakeRequestPending = false;
  private wakeVersion = 0;
  private disposed = false;
  private previousStatus: WorkoutTimerStatus = 'idle';

  constructor() {
    this.element = document.createElement('details');
    this.element.className = 'workout-timer';
    this.element.innerHTML = `
      <summary><span>Timer</span><span class="timer-summary" data-timer-summary><span data-timer-summary-time>00:00</span> · <span data-timer-summary-status role="status">Ready</span></span></summary>
      <div class="timer-body">
        <div class="timer-modes" role="group" aria-label="Timer mode">
          <button type="button" class="btn btn-secondary" data-timer-mode="stopwatch">Stopwatch</button>
          <button type="button" class="btn btn-secondary" data-timer-mode="countdown">Countdown</button>
        </div>
        <div class="timer-display" role="timer" aria-live="off" data-timer-display>00:00</div>
        <p class="timer-status" data-timer-status>Ready</p>
        <fieldset class="timer-duration" hidden>
          <legend>Duration</legend>
          <div class="timer-duration-inputs">
            <label class="field"><span>Minutes</span><input data-timer-minutes type="number" min="0" step="1" value="0" required inputmode="numeric" /></label>
            <label class="field"><span>Seconds</span><input data-timer-seconds type="number" min="0" max="59" step="1" value="30" required inputmode="numeric" /></label>
          </div>
          <div class="timer-presets" role="group" aria-label="Countdown presets">
            <button type="button" class="btn btn-secondary" data-timer-preset="30">30s</button>
            <button type="button" class="btn btn-secondary" data-timer-preset="60">1m</button>
            <button type="button" class="btn btn-secondary" data-timer-preset="120">2m</button>
          </div>
          <p id="workout-timer-error" class="timer-error" role="status" hidden>Enter whole minutes and 0–59 seconds, totaling at least one second.</p>
        </fieldset>
        <div class="timer-actions">
          <button type="button" class="btn" data-timer-action>Start</button>
          <button type="button" class="btn btn-secondary" data-timer-reset>Reset</button>
        </div>
        <label class="timer-sound"><input type="checkbox" data-timer-sound checked /> Sound</label>
      </div>`;
    const options = { signal: this.events.signal };
    this.element.querySelectorAll<HTMLButtonElement>('[data-timer-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        this.timer.setMode(button.dataset.timerMode as WorkoutTimerMode);
        this.refresh();
      }, options);
    });
    this.element.querySelectorAll<HTMLInputElement>('.timer-duration input').forEach((input) => {
      input.addEventListener('input', () => {
        const duration = this.readDuration();
        if (duration !== null) this.timer.setDuration(duration);
        else this.timer.reset();
        this.refresh();
      }, options);
    });
    this.element.querySelectorAll<HTMLButtonElement>('[data-timer-preset]').forEach((button) => {
      button.addEventListener('click', () => {
        const seconds = Number(button.dataset.timerPreset);
        if (!this.timer.setDuration(seconds)) return;
        this.query<HTMLInputElement>('[data-timer-minutes]').value = String(Math.floor(seconds / 60));
        this.query<HTMLInputElement>('[data-timer-seconds]').value = String(seconds % 60);
        this.refresh();
      }, options);
    });
    this.query('[data-timer-action]').addEventListener('click', () => {
      if (this.timer.snapshot().status === 'running') this.timer.pause();
      else {
        if (this.timer.snapshot().mode === 'countdown' && this.readDuration() === null) return;
        this.prepareAudio();
        this.timer.start();
      }
      this.refresh();
    }, options);
    this.query('[data-timer-reset]').addEventListener('click', () => {
      this.timer.reset();
      this.refresh();
    }, options);
    this.query('[data-timer-sound]').addEventListener('change', () => this.prepareAudio(), options);
    document.addEventListener('visibilitychange', () => {
      this.refresh();
      void this.syncWakeLock();
    }, options);
    this.refresh();
  }

  private query<T extends HTMLElement = HTMLElement>(selector: string): T {
    return this.element.querySelector<T>(selector)!;
  }

  private readDuration(): number | null {
    const minutes = this.query<HTMLInputElement>('[data-timer-minutes]');
    const seconds = this.query<HTMLInputElement>('[data-timer-seconds]');
    const duration = minutes.valueAsNumber * 60 + seconds.valueAsNumber;
    return minutes.validity.valid && seconds.validity.valid && duration > 0
      && Number.isSafeInteger(duration * 1000) ? duration : null;
  }

  private refresh(): void {
    if (this.disposed) return;
    const { mode, status, seconds } = this.timer.snapshot();
    const running = status === 'running';
    const countdown = mode === 'countdown';
    const invalid = countdown && this.readDuration() === null;
    const setText = (selector: string, text: string) => {
      const element = this.query(selector);
      if (element.textContent !== text) element.textContent = text;
    };
    setText('[data-timer-display]', formatTimerSeconds(seconds));
    setText('[data-timer-summary-time]', formatTimerSeconds(seconds));
    setText('[data-timer-summary-status]', statusLabels[status]);
    setText('[data-timer-status]', statusLabels[status]);
    this.element.dataset.status = status;
    this.query('[data-timer-display]').setAttribute('aria-label', countdown ? 'Time remaining' : 'Elapsed time');
    this.element.querySelectorAll<HTMLButtonElement>('[data-timer-mode]').forEach((button) => {
      button.disabled = running;
      button.setAttribute('aria-pressed', String(button.dataset.timerMode === mode));
    });
    const duration = this.query<HTMLFieldSetElement>('.timer-duration');
    duration.hidden = !countdown;
    duration.disabled = running;
    this.query('#workout-timer-error').hidden = !invalid;
    duration.querySelectorAll('input').forEach((input) => {
      input.setAttribute('aria-invalid', String(invalid));
      if (invalid) input.setAttribute('aria-describedby', 'workout-timer-error');
      else input.removeAttribute('aria-describedby');
    });
    const action = this.query<HTMLButtonElement>('[data-timer-action]');
    action.disabled = invalid;
    setText('[data-timer-action]', running ? 'Pause' : status === 'paused' ? 'Resume' : 'Start');
    if (running && this.interval === null) this.interval = setInterval(() => this.refresh(), 250);
    if (!running && this.interval !== null) {
      clearInterval(this.interval);
      this.interval = null;
    }
    if (this.previousStatus !== status) {
      this.previousStatus = status;
      void this.syncWakeLock();
    }
  }

  private prepareAudio(): void {
    if (!this.query<HTMLInputElement>('[data-timer-sound]').checked) return;
    try {
      this.audio ??= new AudioContext();
      void this.audio.resume().catch(() => {});
    } catch { /* Timing still works when browser audio is unavailable. */ }
  }

  private playChime(): void {
    if (this.disposed || !this.query<HTMLInputElement>('[data-timer-sound]').checked || this.audio?.state !== 'running') return;
    try {
      const oscillator = this.audio.createOscillator();
      const gain = this.audio.createGain();
      const now = this.audio.currentTime;
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.2, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      oscillator.connect(gain);
      gain.connect(this.audio.destination);
      oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
      oscillator.start(now);
      oscillator.stop(now + 0.5);
    } catch { /* The visual completion state is always available. */ }
  }

  private shouldKeepAwake(): boolean {
    return !this.disposed && !document.hidden && this.timer.snapshot().status === 'running';
  }

  private async syncWakeLock(): Promise<void> {
    if (!this.shouldKeepAwake()) {
      this.wakeVersion++;
      const lock = this.wakeLock;
      this.wakeLock = null;
      if (lock) await lock.release().catch(() => {});
      return;
    }
    if (!navigator.wakeLock || this.wakeLock || this.wakeRequestPending) return;
    const version = this.wakeVersion;
    this.wakeRequestPending = true;
    try {
      const lock = await navigator.wakeLock.request('screen');
      if (version !== this.wakeVersion || !this.shouldKeepAwake()) {
        await lock.release().catch(() => {});
      } else {
        this.wakeLock = lock;
        lock.addEventListener('release', () => {
          if (this.wakeLock === lock) this.wakeLock = null;
        }, { once: true });
      }
    } catch { /* Unsupported devices, battery saver, and denied requests are fine. */ }
    finally {
      this.wakeRequestPending = false;
      // A pause/navigation can invalidate an outstanding request before it resolves.
      if (version !== this.wakeVersion && this.shouldKeepAwake()) void this.syncWakeLock();
    }
  }

  prepareForRender(): void {
    this.restoreFocus = this.element.contains(document.activeElement) ? document.activeElement as HTMLElement : null;
    this.element.remove();
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.element);
    this.refresh();
    this.restoreFocus?.focus({ preventScroll: true });
    this.restoreFocus = null;
  }

  dispose(): void {
    this.disposed = true;
    this.events.abort();
    if (this.interval !== null) clearInterval(this.interval);
    void this.syncWakeLock();
    if (this.audio) void this.audio.close().catch(() => {});
    this.element.remove();
    this.restoreFocus = null;
  }
}
